/**
 * Typed event bus (FS-02 §2.3). Dense subscriber arrays iterated by index; subscribe/unsubscribe
 * during a dispatch is deferred; nested emits are queued and drained, never recursed.
 */
import type { EventMap } from './event-map';

type Handler<T> = (payload: T) => void;

export interface EventBus<M> {
  on<K extends keyof M>(event: K, handler: Handler<M[K]>): () => void;
  once<K extends keyof M>(event: K, handler: Handler<M[K]>): () => void;
  off<K extends keyof M>(event: K, handler: Handler<M[K]>): void;
  emit<K extends keyof M>(event: K, payload: M[K]): void;
  /** Drains queued (nested) emits. Called once per frame after the substep loop. */
  drain(): void;
  clear(): void;
}

const EVENT_QUEUE_MAX = 256;

interface QueuedEmit {
  event: string;
  payload: unknown;
}

class Bus<M> implements EventBus<M> {
  private handlers = new Map<string, Array<Handler<unknown>>>();
  private dispatching = 0;
  private queue: QueuedEmit[] = [];
  private queueHead = 0;

  private list(event: string): Array<Handler<unknown>> {
    let l = this.handlers.get(event);
    if (!l) {
      l = [];
      this.handlers.set(event, l);
    }
    return l;
  }

  on<K extends keyof M>(event: K, handler: Handler<M[K]>): () => void {
    this.list(event as string).push(handler as Handler<unknown>);
    return () => this.off(event, handler);
  }

  once<K extends keyof M>(event: K, handler: Handler<M[K]>): () => void {
    const wrapped: Handler<M[K]> = (p) => {
      this.off(event, wrapped);
      handler(p);
    };
    return this.on(event, wrapped);
  }

  off<K extends keyof M>(event: K, handler: Handler<M[K]>): void {
    const l = this.handlers.get(event as string);
    if (!l) return;
    const i = l.indexOf(handler as Handler<unknown>);
    if (i >= 0) l.splice(i, 1);
  }

  emit<K extends keyof M>(event: K, payload: M[K]): void {
    if (this.dispatching > 0) {
      // Nested emit: queue and drain later (bounded).
      if (this.queue.length - this.queueHead >= EVENT_QUEUE_MAX) return;
      this.queue.push({ event: event as string, payload });
      return;
    }
    this.dispatch(event as string, payload);
  }

  private dispatch(event: string, payload: unknown): void {
    const l = this.handlers.get(event);
    if (!l || l.length === 0) return;
    this.dispatching++;
    // Snapshot length so handlers added during dispatch don't run this pass.
    const n = l.length;
    for (let i = 0; i < n; i++) {
      const h = l[i];
      if (h) h(payload);
    }
    this.dispatching--;
  }

  drain(): void {
    while (this.queueHead < this.queue.length) {
      const item = this.queue[this.queueHead++]!;
      this.dispatch(item.event, item.payload);
    }
    this.queue.length = 0;
    this.queueHead = 0;
  }

  clear(): void {
    this.handlers.clear();
    this.queue.length = 0;
    this.queueHead = 0;
  }
}

export function createEventBus(): EventBus<EventMap> {
  return new Bus<EventMap>();
}
